import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MOCK_WORKOUTS, getExerciseById } from '../utils/mockData';
import Icon from '../components/shared/Icon';
import './WorkoutDetailPage.css';

function formatWU(wu) {
  if (!wu) return '—';
  return wu >= 1000 ? `${(wu / 1000).toFixed(1)}k WU` : `${wu} WU`;
}

function formatSetLabel(ex) {
  const parts = [];
  if (ex.reps > 0) parts.push(`${ex.reps} reps`);
  if (ex.weightLbs > 0) parts.push(`${ex.weightLbs} lbs`);
  if (ex.durationSec > 0) {
    const mins = Math.floor(ex.durationSec / 60);
    const secs = ex.durationSec % 60;
    parts.push(mins > 0 ? `${mins}m${secs > 0 ? ` ${secs}s` : ''}` : `${secs}s`);
  }
  return parts.join(' · ');
}

export default function WorkoutDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const workout = MOCK_WORKOUTS.find((w) => w.id === id);

  const [checked, setChecked] = useState({});
  const [notes, setNotes] = useState({});

  if (!workout) {
    return (
      <div className="workout-detail__not-found">
        <p className="workout-detail__not-found-text">Workout not found.</p>
        <button className="btn btn--ghost btn--sm" onClick={() => navigate('/app/workouts')} type="button">
          ← Back to Workouts
        </button>
      </div>
    );
  }

  const totalSets = workout.exercises.reduce((sum, ex) => sum + ex.sets, 0);
  const completedSets = Object.values(checked).filter(Boolean).length;
  const progress = totalSets > 0 ? completedSets / totalSets : 0;
  const allDone = completedSets === totalSets && totalSets > 0;

  function toggleCheck(key) {
    setChecked((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  return (
    <div className="workout-detail">

      {/* ── Header ──────────────────────────────── */}
      <div className="workout-detail__head">
        <button
          className="workout-detail__back"
          onClick={() => navigate('/app/workouts')}
          type="button"
        >
          <Icon name="chevron-left" size={16} /> Workouts
        </button>

        <div className="workout-detail__title-block">
          <h1 className="workout-detail__title">{workout.name}</h1>
          {workout.description && (
            <p className="workout-detail__desc">{workout.description}</p>
          )}
          <div className="workout-detail__meta">
            <span><Icon name="clock" size={13} /> {workout.duration} min</span>
            <span><Icon name="zap" size={13} /> {formatWU(workout.workOutputTarget)}</span>
            {workout.scheduledDate && (
              <span className="workout-detail__meta-date">{workout.scheduledDate}</span>
            )}
          </div>
          {workout.tags?.length > 0 && (
            <div className="workout-detail__tags">
              {workout.tags.map((t) => (
                <span key={t} className="workout-detail__tag">{t}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Progress ─────────────────────────────── */}
      <div className="workout-detail__progress">
        <div className="workout-detail__progress-track">
          <div
            className={`workout-detail__progress-fill ${allDone ? 'workout-detail__progress-fill--done' : ''}`}
            style={{ width: `${progress * 100}%` }}
          />
        </div>
        <span className="workout-detail__progress-label">
          {allDone ? '✓ Complete' : `${completedSets} / ${totalSets} sets`}
        </span>
      </div>

      {/* ── Exercise List ────────────────────────── */}
      <div className="workout-detail__exercises">
        {workout.exercises.map((ex, exIdx) => {
          const exData = getExerciseById(ex.exerciseId);
          const exDoneCount = Array.from({ length: ex.sets }).filter(
            (_, si) => checked[`${exIdx}-${si}`]
          ).length;
          const exAllDone = exDoneCount === ex.sets;

          return (
            <div key={exIdx} className={`workout-detail__exercise ${exAllDone ? 'workout-detail__exercise--done' : ''}`}>
              <div className="workout-detail__ex-head">
                <div className="workout-detail__ex-left">
                  <h3 className="workout-detail__ex-name">{exData?.name || ex.exerciseId}</h3>
                  <span className="workout-detail__ex-category">{exData?.category}</span>
                </div>
                <span className="workout-detail__ex-progress">
                  {exDoneCount}/{ex.sets}
                </span>
              </div>

              <div className="workout-detail__sets">
                {Array.from({ length: ex.sets }).map((_, setIdx) => {
                  const key = `${exIdx}-${setIdx}`;
                  const done = !!checked[key];
                  return (
                    <button
                      key={setIdx}
                      className={`workout-detail__set ${done ? 'workout-detail__set--done' : ''}`}
                      onClick={() => toggleCheck(key)}
                      type="button"
                    >
                      <span className="workout-detail__set-box">
                        {done && <Icon name="check" size={11} />}
                      </span>
                      <span className="workout-detail__set-text">
                        Set {setIdx + 1}{formatSetLabel(ex) ? ` · ${formatSetLabel(ex)}` : ''}
                      </span>
                    </button>
                  );
                })}
              </div>

              <textarea
                className="workout-detail__notes"
                placeholder={`Notes for ${exData?.name || 'this exercise'}…`}
                value={notes[exIdx] || ''}
                onChange={(e) => setNotes((prev) => ({ ...prev, [exIdx]: e.target.value }))}
                rows={2}
              />
            </div>
          );
        })}
      </div>

      {/* ── Coach: Assign CTA ────────────────────── */}
      {user?.role === 'coach' && (
        <div className="workout-detail__assign">
          <button className="btn btn--outline" type="button">
            <Icon name="users" size={15} /> Assign Workout
          </button>
        </div>
      )}

    </div>
  );
}
